<script setup lang="ts">
import {
  APP_STORE_URL,
  GOOGLE_PLAY_URL,
  WEB_APP_URL
} from '~/constants/website'
import { Motion } from 'motion-v'
import type { HTMLAttributes } from 'vue'

const props = withDefaults(
  defineProps<{
    title?: string
    subtitle?: string
    description?: string
    heroImage?: string
    buttonLink?: string
    buttonClass?: HTMLAttributes['class']
    buttonLabel?: string
  }>(),
  {
    title: 'Mulai Kirim Paketmu Sekarang!',
    subtitle: 'Satu Solusi untuk Semua #PastiAman',
    description:
      'Jadikan pengalaman pengiriman paket lebih mudah dengan aplikasi KiriminAja.',
    buttonLink: undefined,
    heroImage: undefined,
    buttonClass: undefined,
    buttonLabel: 'Daftar Gratis'
  }
)
const mobileView = useState('mobileView', () => false)

const route = useRoute()
if (route.query.platform) {
  mobileView.value = true
}

const { $utm } = useNuxtApp()
</script>

<template>
  <section
    v-if="!mobileView"
    class="bg-background py-6 lg:py-12 2xl:py-24 relative"
  >
    <UiBgGrid class="via-magenta-200 from-primary-200 to-cyan-200" />

    <UiContainer
      class="grid xl:flex h-full items-center relative overflow-hidden"
    >
      <div class="flex xl:grow flex-col gap-3 lg:gap-7">
        <Motion
          class="flex flex-col gap-2 xl:max-w-8/12"
          :initial="{ opacity: 0, translateY: 40 }"
          :while-in-view="{ opacity: 1, translateY: 0 }"
          :in-view-options="{ once: true }"
        >
          <UiSectionTitle
            :title="title"
            :sub-title="subtitle"
            title-as="h2"
            sub-title-as="p"
            :description="description"
          />
        </Motion>
        <slot name="action">
          <Motion
            v-if="buttonLink"
            :initial="{ opacity: 0, translateY: 40 }"
            :while-in-view="{ opacity: 1, translateY: 0 }"
            :in-view-options="{ once: true }"
            class="inline-flex flex-col sm:items-center sm:flex-row pb-2"
          >
            <UiButton
              :class="props.buttonClass"
              :to="$utm.utmUrl(props.buttonLink)"
              :label="props.buttonLabel"
            />
          </Motion>
          <template v-else>
            <div id="cta" class="flex items-center gap-4">
              <Motion
                class="inline-flex flex-col gap-4"
                :initial="{ opacity: 0, translateY: 40 }"
                :while-in-view="{ opacity: 1, translateY: 0 }"
                :in-view-options="{ once: true }"
              >
                <NuxtLink
                  :to="$utm.utmUrl(GOOGLE_PLAY_URL, undefined, true)"
                  rel="noopener noreferrer"
                >
                  <NuxtImg
                    alt="google-play"
                    src="/images/home-v4/playstore.webp"
                    width="177px"
                    height="52px"
                    class="max-w-full lg:h-13 rounded-xl border border-transparent dark:border-outline-variant"
                  />
                </NuxtLink>
                <NuxtLink
                  :to="$utm.utmUrl(APP_STORE_URL)"
                  rel="noopener noreferrer"
                >
                  <NuxtImg
                    alt="apple-store"
                    src="/images/home-v4/appstore2.webp"
                    width="177px"
                    height="52px"
                    class="max-w-full lg:h-13 rounded-xl border border-transparent dark:border-outline-variant"
                  />
                </NuxtLink>
              </Motion>
              <div class="w-px h-1/2 bg-outline"></div>
              <CtaQr />
            </div>
            <p class="text-lg">
              Atau versi
              <NuxtLink
                :to="$utm.utmUrl(WEB_APP_URL)"
                rel="noopener noreferrer"
                class="font-bold underline"
              >
                Web Dashboard
              </NuxtLink>
            </p>
          </template>
        </slot>
      </div>
      <slot name="hero">
        <NuxtImg
          alt="Aplikasi Kirim Paket"
          width="520px"
          height="520px"
          class="hidden lg:block"
          :src="props.heroImage || '/images/aplikasi-mobile-banner-3.webp'"
        />
      </slot>
    </UiContainer>
  </section>
</template>
